import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  Divider,
  Autocomplete,
  FormHelperText,
  CircularProgress,
  Chip,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import NotificationValidationSchema from "./Validations/BannerValidation";
import api from "../axiosInstance";
import { toast } from "react-toastify";
import useUniversitiesList from "../hooks/useUniversitiesList";
import DeleteIcon from "@mui/icons-material/Delete";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import {
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
  BUTTON_STYLES,
  FORM_STYLES,
} from "../styles/componentStyles";

const EMOJI_FONT_STACK =
  '"Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji","Segoe UI Symbol",sans-serif';

function NotificationModal({
  open,
  onClose,
  notificationEdit,
  selectedUsersNotifications,
  selectedUsers,
  onSuccess,
}) {
  const { universityList, courses, semesters, regulations } =
    useUniversitiesList({});

  const [userCount, setUserCount] = useState(0);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  console.log(notificationEdit);

  //  Form vlaidation using useFormik hook
  const formik = useFormik({
    initialValues: {
      title: notificationEdit?.title || "",
      message: notificationEdit?.message || "",
      image: notificationEdit?.image || "https://placehold.co/600x400",
      targetType:
        notificationEdit?.targetType ||
        (selectedUsersNotifications && selectedUsersNotifications.length > 0) ||
        (selectedUsers && selectedUsers.length > 0)
          ? "SPECIFIC"
          : "ALL",
      userIds:
        selectedUsersNotifications?.length > 0
          ? selectedUsersNotifications
          : selectedUsers?.length > 0
            ? selectedUsers
            : [],
      notification_search: notificationEdit?.notification_search || "",
      universityId: notificationEdit?.universityId || "",
      universityIds:
        notificationEdit?.universityIds ||
        (notificationEdit?.universityId != null
          ? [notificationEdit.universityId]
          : []),
      courseIds: notificationEdit?.courseIds || [],
      regulationIds:
        notificationEdit?.regulationIds ||
        (notificationEdit?.regulationId != null
          ? [notificationEdit.regulationId]
          : []),
      semesterId: notificationEdit?.semesterId || "",
      semesterIds:
        notificationEdit?.semesterIds ||
        (notificationEdit?.semesterId != null
          ? [notificationEdit.semesterId]
          : []),
      purchaseStatus: notificationEdit?.purchaseStatus || "ALL",
      notification_trigger: notificationEdit?.notification_trigger || "",
      notificationType: notificationEdit?.notificationType || "",
      scheduled_date_and_time: notificationEdit?.scheduled_date_and_time
        ? notificationEdit.scheduled_date_and_time
        : [""],
      recurDatesAndTimes:
        notificationEdit?.recurDatesAndTimes?.length > 0
          ? notificationEdit.recurDatesAndTimes
          : [""],
      tempDateTime: "",
    },

    validationSchema: NotificationValidationSchema.NotificationValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm, setSubmitting, setErrors }) => {
      console.log("=== FORM SUBMISSION STARTED ===");
      console.log("Form submitted with values:", values);
      setSubmitting(true);

      try {
        // Exclude userIds from the payload
        const { userIds, ...payload } = values;

        // Only include fields based on targetType
        const cleanPayload = {
          title: payload.title,
          message: payload.message,
          image: payload.image || "",
          targetType: payload.targetType,
          notification_trigger: payload.notification_trigger,
          notificationType: payload.notificationType,
          scheduled_date_and_time: payload.scheduled_date_and_time,
          recurDatesAndTimes: payload.recurDatesAndTimes,
        };

        // Add fields based on targetType
        if (payload.targetType === "SPECIFIC") {
          if (payload.notification_search)
            cleanPayload.notification_search = payload.notification_search;
          if (payload.universityIds?.length > 0)
            cleanPayload.universityIds = payload.universityIds.map(Number);
          if (payload.courseIds?.length > 0)
            cleanPayload.courseIds = payload.courseIds.map(Number);

          if (payload.regulationIds?.length > 0)
            cleanPayload.regulationIds = payload.regulationIds.map(Number);
          if (payload.semesterIds?.length > 0)
            cleanPayload.semesterIds = payload.semesterIds.map(Number);
          if (payload.purchaseStatus && payload.purchaseStatus !== "ALL") {
            cleanPayload.purchaseStatus = payload.purchaseStatus;
          }
        } else if (payload.targetType === "UNIVERSITY") {
          if (payload.universityId)
            cleanPayload.universityId = Number(payload.universityId);
        } else if (payload.targetType === "COURSE") {
          if (payload.courseIds?.length > 0)
            cleanPayload.courseIds = payload.courseIds.map(Number);
        } else if (payload.targetType === "SEMESTER") {
          if (payload.semesterId)
            cleanPayload.semesterId = Number(payload.semesterId);
        }

        console.log("Sending payload:", cleanPayload);

        let response;
        // If it's Edit data it will call Update api
        if (notificationEdit?.id) {
          response = await api.put(
            `/api/v1/notifications/${notificationEdit?.id}`,
            cleanPayload,
          );
          onSuccess(response.data.data);
        }
        // Else it's Add data it will call Add api
        else {
          response = await api.post("/api/v1/notifications", cleanPayload);
        }

        console.log("API Response:", response);

        // Show success toast
        toast.success(
          `Notifications ${
            notificationEdit?.id ? "updated" : "created"
          } successfully!`,
          {
            position: "top-right",
            autoClose: 2000,
          },
        );

        resetForm();
        setUserCount(0);
        onClose();
      } catch (error) {
        console.error("API Error Details:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to submit. Please try again.";
        toast.error(errorMessage, {
          autoClose: 3000,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(formik.values.notification_search || "");
    }, 500);
    return () => clearTimeout(handler);
  }, [formik.values.notification_search]);

  // Fetch user count based on filters
  useEffect(() => {
    const fetchUserCount = async () => {
      // Only fetch count for target types that need filters
      if (
        formik.values.targetType === "ALL" ||
        (!formik.values.universityId &&
          !formik.values.universityIds?.length &&
          !formik.values.courseIds &&
          !formik.values.semesterId &&
          !formik.values.semesterIds?.length &&
          !formik.values.regulationIds?.length &&
          !debouncedSearch &&
          formik.values.purchaseStatus === "ALL" &&
          formik.values.targetType !== "SPECIFIC")
      ) {
        // For ALL or when no filters are set, we might want to skip or get total count
        if (formik.values.targetType === "ALL") {
          setUserCount(0); // Will be set by backend or skip
          return;
        }
      }

      setIsLoadingCount(true);
      try {
        const params = {
          page: 0,
          size: 1, // Just need count, not data
        };

        if (formik.values.targetType === "SPECIFIC") {
          if (debouncedSearch) params.search = debouncedSearch;
          if (formik.values.universityIds?.length > 0)
            params.universityIds = formik.values.universityIds;
          if (formik.values.courseIds)
            params.courseIds = formik.values.courseIds;
          if (formik.values.regulationIds?.length > 0)
            params.regulationIds = formik.values.regulationIds;
          if (formik.values.semesterIds?.length > 0)
            params.semesterIds = formik.values.semesterIds;
          if (
            formik.values.purchaseStatus &&
            formik.values.purchaseStatus !== "ALL"
          ) {
            params.purchaseStatus = formik.values.purchaseStatus;
          }
        } else if (formik.values.targetType === "UNIVERSITY") {
          if (formik.values.universityId)
            params.universityId = formik.values.universityId;
        } else if (formik.values.targetType === "COURSE") {
          if (formik.values.courseIds?.length > 0)
            params.courseIds = formik.values.courseIds;
        } else if (formik.values.targetType === "SEMESTER") {
          if (formik.values.semesterId)
            params.semesterId = formik.values.semesterId;
        }

        const response = await api.get("/api/userDetails/getAllUserDetails", {
          params,
          paramsSerializer: (params) => {
            const searchParams = new URLSearchParams();

            Object.keys(params).forEach((key) => {
              if (Array.isArray(params[key])) {
                params[key].forEach((value) => {
                  searchParams.append(key, value);
                });
              } else {
                searchParams.append(key, params[key]);
              }
            });

            return searchParams.toString();
          },
        });

        const data = response.data;
        setUserCount(
          typeof data?.totalElements === "number" ? data.totalElements : 0,
        );
      } catch (err) {
        console.error("Error fetching user count:", err);
        setUserCount(0);
      } finally {
        setIsLoadingCount(false);
      }
    };

    // Only fetch if modal is open and targetType is not ALL
    if (open && formik.values.targetType !== "ALL") {
      fetchUserCount();
    } else if (formik.values.targetType === "ALL") {
      setUserCount(0); // Reset for ALL
    }
  }, [
    open,
    formik.values.targetType,
    formik.values.universityId,
    formik.values.universityIds,
    formik.values.courseIds,
    formik.values.regulationIds,
    formik.values.semesterId,
    formik.values.semesterIds,
    formik.values.purchaseStatus,
    debouncedSearch,
  ]);

  const setEmojiFriendlyValue = (fieldName, rawValue = "") => {
    if (typeof rawValue !== "string") {
      formik.setFieldValue(fieldName, "");
      return;
    }
    formik.setFieldValue(fieldName, rawValue.normalize("NFC"));
  };

  const handleEmojiChange = (fieldName) => (event) => {
    setEmojiFriendlyValue(fieldName, event?.target?.value ?? "");
  };

  const handleEmojiPaste = (fieldName) => (event) => {
    const pastedText = event.clipboardData?.getData("text/plain");

    if (!pastedText) {
      return;
    }

    event.preventDefault();

    const inputElement = event.target;
    const selectionStart = inputElement.selectionStart ?? 0;
    const selectionEnd = inputElement.selectionEnd ?? selectionStart;
    const currentValue = formik.values[fieldName] ?? "";

    const nextValue =
      currentValue.slice(0, selectionStart) +
      pastedText +
      currentValue.slice(selectionEnd);

    setEmojiFriendlyValue(fieldName, nextValue);

    const caretPosition = selectionStart + pastedText.length;
    requestAnimationFrame(() => {
      inputElement.selectionStart = caretPosition;
      inputElement.selectionEnd = caretPosition;
    });
  };

  const getEmojiInputProps = (fieldName) => ({
    onPaste: handleEmojiPaste(fieldName),
    style: { fontFamily: EMOJI_FONT_STACK },
  });

  return (
    <>
      <Modal open={open} onClose={onClose} disableScrollLock>
        <Box
          sx={{
            ...MODAL_STYLES.standard,
            width: { xs: 340, md: 600 },
            maxHeight: "90vh",
            overflowY: "auto",
          }}
          component="form"
          onSubmit={formik.handleSubmit}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            {notificationEdit && Object.keys(notificationEdit).length > 0
              ? "Update Notifications"
              : "Add Notifications"}
          </Typography>

          <Box sx={FORM_STYLES.row}>
            {selectedUsers && selectedUsers.length >= 0 && (
              <FormControl
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...FORM_STYLES.field.sx,
                  minWidth: 300,
                }}
                size="medium"
              >
                <InputLabel>Selected Users</InputLabel>
                <Select
                  multiple
                  disabled
                  label="Selected Users"
                  name="userIds"
                  value={formik.values.userIds || []}
                  onChange={(e) =>
                    formik.setFieldValue("userIds", e.target.value)
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value, index) => (
                        <Chip key={index} label={value} size="medium" />
                      ))}
                    </Box>
                  )}
                  sx={{ py: 0.5 }}
                >
                  {(selectedUsers || []).map((userId) => (
                    <MenuItem key={userId} value={userId}>
                      {userId}
                    </MenuItem>
                  ))}
                </Select>

                {formik.touched.userIds && formik.errors.userIds && (
                  <FormHelperText sx={{ color: "#d9322f" }}>
                    {formik.errors.userIds}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          </Box>
          <Box sx={FORM_STYLES.container}>
            <TextField
              size="small"
              label="Title"
              name="title"
              value={formik.values.title}
              onChange={handleEmojiChange("title")}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              {...TEXT_FIELD_STYLES.standard}
              required
              inputProps={getEmojiInputProps("title")}
            />

            <TextField
              size="small"
              multiline
              minRows={2}
              label="Message"
              name="message"
              value={formik.values.message}
              onChange={handleEmojiChange("message")}
              onBlur={formik.handleBlur}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
              {...TEXT_FIELD_STYLES.standard}
              inputProps={getEmojiInputProps("message")}
            />
            <Box sx={FORM_STYLES.row}>
              <TextField
                size="small"
                label="Image"
                name="image"
                value={formik.values.image}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.image && Boolean(formik.errors.image)}
                helperText={formik.touched.image && formik.errors.image}
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...TEXT_FIELD_STYLES.standard.sx,
                  ...FORM_STYLES.field.sx,
                }}
              />
              <FormControl
                {...TEXT_FIELD_STYLES.standard}
                sx={{
                  ...FORM_STYLES.field.sx,
                  minWidth: 300,
                }}
                size="small"
                error={
                  formik.touched.targetType && Boolean(formik.errors.targetType)
                }
              >
                <InputLabel>Target Type</InputLabel>
                <Select
                  label="Target Type"
                  name="targetType"
                  value={
                    selectedUsersNotifications &&
                    selectedUsersNotifications.length > 0
                      ? "SPECIFIC"
                      : formik.values.targetType
                  }
                  onChange={(e) => {
                    formik.handleChange(e);
                    // Reset dependent fields when targetType changes
                    formik.setFieldValue("universityId", "");
                    formik.setFieldValue("universityIds", []);
                    formik.setFieldValue("courseIds", []);
                    formik.setFieldValue("regulationIds", []);
                    formik.setFieldValue("semesterId", "");
                    formik.setFieldValue("semesterIds", []);
                    formik.setFieldValue("notification_search", "");
                    formik.setFieldValue("purchaseStatus", "ALL");
                  }}
                  onBlur={formik.handleBlur}
                  disabled={
                    selectedUsersNotifications &&
                    selectedUsersNotifications.length > 0
                  } // disable if data present
                >
                  <MenuItem value="ALL">ALL</MenuItem>
                  <MenuItem value="SPECIFIC">SPECIFIC</MenuItem>
                  <MenuItem value="UNIVERSITY">UNIVERSITY</MenuItem>
                  <MenuItem value="COURSE">COURSE</MenuItem>
                  <MenuItem value="SEMESTER">SEMESTER</MenuItem>
                </Select>

                {formik.touched.targetType && formik.errors.targetType && (
                  <FormHelperText sx={{ color: "#d9322f" }}>
                    {formik.errors.targetType}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Box>

          {/* Conditional Fields based on targetType */}
          {formik.values.targetType === "SPECIFIC" && (
            <Box sx={FORM_STYLES.container}>
              <TextField
                size="small"
                label="Notification Search (Custom Query)"
                name="notification_search"
                value={formik.values.notification_search}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                {...TEXT_FIELD_STYLES.standard}
                helperText="Optional: Custom Notification search query for specific users"
              />
              {isLoadingCount ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  <CircularProgress
                    size={14}
                    sx={{ mr: 1, verticalAlign: "middle" }}
                  />
                  Loading user count...
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Users matching filters: <strong>{userCount}</strong>
                </Typography>
              )}
              <Grid container columnSpacing={1}>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    size="small"
                    fullWidth
                    {...TEXT_FIELD_STYLES.standard}
                  >
                    <InputLabel>University</InputLabel>
                    <Select
                      multiple
                      label="University"
                      name="universityIds"
                      value={formik.values.universityIds || []}
                      onChange={(e) => {
                        formik.setFieldValue("universityIds", e.target.value);
                        formik.setFieldValue("courseIds", []);
                        formik.setFieldValue("regulationIds", []);
                        formik.setFieldValue("semesterIds", []);
                      }}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {selected.map((value) => {
                            const university = universityList.find(
                              (u) => Number(u.id) === Number(value),
                            );

                            return (
                              <Chip
                                key={value}
                                label={university?.name}
                                size="small"
                                onDelete={() => {
                                  const updated =
                                    formik.values.universityIds.filter(
                                      (id) => Number(id) !== Number(value),
                                    );

                                  formik.setFieldValue("universityIds", updated);
                                }}
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {universityList.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <FormControl
                    size="small"
                    fullWidth
                    {...TEXT_FIELD_STYLES.standard}
                  >
                    <InputLabel>Course</InputLabel>
                    <Select
                      label="Course"
                      name="courseIds"
                      value={formik.values.courseIds || ""}
                      onChange={(e) => {
                        formik.handleChange(e);
                        formik.setFieldValue("regulationId", "");
                        formik.setFieldValue("semesterId", "");
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {courses
                        .filter(
                          (course) =>
                            !formik.values.universityId ||
                            course.universityId === formik.values.universityId,
                        )
                        .map((course) => (
                          <MenuItem key={course.id} value={course.id}>
                            {course.courseName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <FormControl
                    size="small"
                    fullWidth
                    {...TEXT_FIELD_STYLES.standard}
                  >
                    <InputLabel>Course</InputLabel>

                    <Select
                      multiple
                      label="Course"
                      name="courseIds"
                      value={formik.values.courseIds || []}
                      onChange={(e) => {
                        formik.setFieldValue("courseIds", e.target.value);

                        // Reset regulation when course changes
                        formik.setFieldValue("regulationIds", []);
                        formik.setFieldValue("semesterIds", []);
                      }}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {selected.map((value) => {
                            const course = courses.find(
                              (c) => Number(c.id) === Number(value),
                            );

                            return (
                              <Chip
                                key={value}
                                label={course?.courseName}
                                size="small"
                                onDelete={() => {
                                  const updated =
                                    formik.values.courseIds.filter(
                                      (id) => Number(id) !== Number(value),
                                    );

                                  formik.setFieldValue("courseIds", updated);
                                }}
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {courses
                        .filter(
                          (course) =>
                            !formik.values.universityIds?.length ||
                            formik.values.universityIds
                              .map(Number)
                              .includes(Number(course.universityId)),
                        )

                        .map((course) => (
                          <MenuItem key={course.id} value={course.id}>
                            {course.courseName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    size="small"
                    fullWidth
                    {...TEXT_FIELD_STYLES.standard}
                  >
                    <InputLabel>Regulation</InputLabel>

                    <Select
                      multiple
                      label="Regulation"
                      name="regulationIds"
                      value={formik.values.regulationIds || []}
                      onChange={(e) => {
                        formik.setFieldValue("regulationIds", e.target.value);
                      }}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {selected.map((value) => {
                            const regulation = regulations.find(
                              (r) => Number(r.id) === Number(value),
                            );

                            return (
                              <Chip
                                key={value}
                                label={regulation?.regulationName}
                                size="small"
                                onDelete={() => {
                                  const updated =
                                    formik.values.regulationIds.filter(
                                      (id) => Number(id) !== Number(value),
                                    );

                                  formik.setFieldValue("regulationIds", updated);
                                }}
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {regulations
                        .filter((reg) =>
                          formik.values.courseIds.includes(
                            Number(reg.courseId),
                          ),
                        )

                        .map((reg) => (
                          <MenuItem key={reg.id} value={reg.id}>
                            {reg.regulationName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    size="small"
                    fullWidth
                    {...TEXT_FIELD_STYLES.standard}
                  >
                    <InputLabel>Semester</InputLabel>
                    <Select
                      multiple
                      label="Semester"
                      name="semesterIds"
                      value={formik.values.semesterIds || []}
                      onChange={(e) => {
                        formik.setFieldValue("semesterIds", e.target.value);
                      }}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {selected.map((value) => {
                            const semester = semesters.find(
                              (s) => Number(s.id) === Number(value),
                            );

                            return (
                              <Chip
                                key={value}
                                label={semester?.name}
                                size="small"
                                onDelete={() => {
                                  const updated =
                                    formik.values.semesterIds.filter(
                                      (id) => Number(id) !== Number(value),
                                    );

                                  formik.setFieldValue("semesterIds", updated);
                                }}
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {semesters
                        .filter(
                          (sem) =>
                            !formik.values.courseIds?.length ||
                            courses.some(
                              (course) =>
                                formik.values.courseIds.includes(course.id) &&
                                course?.semesters?.includes(sem.id),
                            ),
                        )
                        .map((sem) => (
                          <MenuItem key={sem.id} value={sem.id}>
                            {sem.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormControl
                    size="small"
                    fullWidth
                    {...TEXT_FIELD_STYLES.standard}
                  >
                    <InputLabel>Purchase Status</InputLabel>
                    <Select
                      label="Purchase Status"
                      name="purchaseStatus"
                      value={formik.values.purchaseStatus}
                      onChange={formik.handleChange}
                    >
                      <MenuItem value="ALL">All Users</MenuItem>
                      <MenuItem value="PURCHASED">Purchased</MenuItem>
                      <MenuItem value="NOT_PURCHASED">Not Purchased</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container columnSpacing={1}>
                <Grid item xs={12} sm={12}>
                  <FormControl
                    size="small"
                    fullWidth
                    {...TEXT_FIELD_STYLES.standard}
                  >
                    <InputLabel>Notification Trigger</InputLabel>
                    <Select
                      label="Notification Trigger"
                      name="notification_trigger"
                      value={formik.values.notification_trigger}
                      onChange={(e) => {
                        const trigger = e.target.value;
                        formik.setFieldValue("notification_trigger", trigger);

                        if (trigger === "send_now") {
                          // Clear ALL scheduling fields
                          formik.setFieldValue("notificationType", "");
                          formik.setFieldValue("scheduled_date_and_time", [""]);
                          formik.setFieldValue("recurDatesAndTimes", [""]);
                          formik.setFieldValue("tempDateTime", "");
                        }
                      }}
                    >
                      <MenuItem value="">Select Notification Trigger</MenuItem>
                      <MenuItem value="send_now">Send Now</MenuItem>
                      <MenuItem value="send_later">Send Later</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formik.values.notification_trigger === "send_later" && (
                  <Grid item xs={12} sm={12}>
                    <FormControl
                      size="small"
                      fullWidth
                      {...TEXT_FIELD_STYLES.standard}
                    >
                      <InputLabel>Notification Type</InputLabel>
                      <Select
                        label="notification Type"
                        name="notificationType"
                        value={formik.values.notificationType}
                        onChange={(e) => {
                          const value = e.target.value;
                          formik.setFieldValue("notificationType", value);

                          // If Non Recurring → Clear Recurring values
                          if (value === "NON_RECURRING") {
                            formik.setFieldValue("recurDatesAndTimes", [""]);
                            formik.setFieldValue("tempDateTime", "");
                          }

                          // If Recurring → Clear Non-Recurring value
                          if (value === "RECURRING") {
                            formik.setFieldValue("scheduled_date_and_time", [
                              "",
                            ]);
                          }
                        }}
                      >
                        <MenuItem value="">Select Notification Type</MenuItem>
                        <MenuItem value="NON_RECURRING">NON RECURRING</MenuItem>
                        <MenuItem value="RECURRING">RECURRING</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {formik.values.notification_trigger === "send_later" &&
                  formik.values.notificationType === "NON_RECURRING" && (
                    <Grid item xs={12} sm={12}>
                      <TextField
                        label="Schedule Date & Time"
                        type="datetime-local"
                        name="scheduled_date_and_time"
                        value={formik.values.scheduled_date_and_time[0] || ""}
                        onChange={(e) => {
                          const formatted = e.target.value.replace("T", " ");
                          formik.setFieldValue("scheduled_date_and_time", [
                            formatted,
                          ]);
                        }}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        {...TEXT_FIELD_STYLES.standard}
                      />
                    </Grid>
                  )}
                {formik.values.notification_trigger === "send_later" &&
                  formik.values.notificationType === "RECURRING" && (
                    <Grid container columnSpacing={1} item xs={12} sm={12}>
                      <Grid item xs={11} sm={11}>
                        <TextField
                          label="Schedule Dates & Time"
                          type="datetime-local"
                          name="tempDateTime"
                          value={formik.values.tempDateTime}
                          onChange={formik.handleChange}
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          {...TEXT_FIELD_STYLES.standard}
                        />
                      </Grid>
                      <Grid item xs={1} sm={1}>
                        <Tooltip title="Add" arrow placement="top">
                          <IconButton
                            color="success"
                            onClick={() => {
                              if (!formik.values.tempDateTime) return;

                              const newDate =
                                formik.values.tempDateTime.replace("T", " ");

                              formik.setFieldValue("recurDatesAndTimes", [
                                ...formik.values.recurDatesAndTimes,
                                newDate,
                              ]);

                              formik.setFieldValue("tempDateTime", "");
                            }}
                            size="meduim "
                            sx={{
                              "&:hover": {
                                color: "info",
                              },
                              marginTop: "17px",
                            }}
                          >
                            <AddToPhotosIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      {/* Show all added dates */}
                      <Box sx={{ ml: 1.5 }}>
                        {formik.values.recurDatesAndTimes

                          .filter((dt) => dt && dt.trim() !== "")
                          .map((dt, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 0.2,
                              }}
                            >
                              <Typography>{dt}</Typography>

                              <Tooltip title="remove" arrow placement="top">
                                <IconButton
                                  color="error"
                                  onClick={() => {
                                    const updated = [
                                      ...formik.values.recurDatesAndTimes,
                                    ];
                                    updated.splice(index, 1);
                                    formik.setFieldValue(
                                      "recurDatesAndTimes",
                                      updated,
                                    );
                                  }}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "error.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </Box>
          )}

          {formik.values.targetType === "UNIVERSITY" && (
            <Box sx={FORM_STYLES.container}>
              <FormControl
                fullWidth
                {...TEXT_FIELD_STYLES.standard}
                size="small"
              >
                <InputLabel>University</InputLabel>
                <Select
                  label="University"
                  name="universityId"
                  value={formik.values.universityId || ""}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {universityList.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isLoadingCount ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  <CircularProgress
                    size={14}
                    sx={{ mr: 1, verticalAlign: "middle" }}
                  />
                  Loading user count...
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Users in this university: <strong>{userCount}</strong>
                </Typography>
              )}
            </Box>
          )}

          {formik.values.targetType === "COURSE" && (
            <Box sx={FORM_STYLES.container}>
              <FormControl
                fullWidth
                {...TEXT_FIELD_STYLES.standard}
                size="small"
              >
                <InputLabel>Course</InputLabel>
                <Select
                  multiple
                  label="Course"
                  name="courseIds"
                  value={formik.values.courseIds || []}
                  onChange={(e) =>
                    formik.setFieldValue("courseIds", e.target.value)
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {courses.map((course) => (
                    <MenuItem key={course.id} value={course.id}>
                      {course.courseName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isLoadingCount ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  <CircularProgress
                    size={14}
                    sx={{ mr: 1, verticalAlign: "middle" }}
                  />
                  Loading user count...
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Users in this course: <strong>{userCount}</strong>
                </Typography>
              )}
            </Box>
          )}

          {formik.values.targetType === "SEMESTER" && (
            <Box sx={FORM_STYLES.container}>
              <FormControl
                fullWidth
                {...TEXT_FIELD_STYLES.standard}
                size="small"
              >
                <InputLabel>Semester</InputLabel>
                <Select
                  label="Semester"
                  name="semesterId"
                  value={formik.values.semesterId || ""}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {semesters.map((sem) => (
                    <MenuItem key={sem.id} value={sem.id}>
                      {sem.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isLoadingCount ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  <CircularProgress
                    size={14}
                    sx={{ mr: 1, verticalAlign: "middle" }}
                  />
                  Loading user count...
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Users in this semester: <strong>{userCount}</strong>
                </Typography>
              )}
            </Box>
          )}

          <Box
            sx={FORM_STYLES.actions}
            style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
          >
            <Button onClick={onClose} {...BUTTON_STYLES.secondary}>
              Cancel
            </Button>
            <Button
              type="submit"
              {...BUTTON_STYLES.primary}
              disabled={
                formik.isSubmitting ||
                !(
                  formik.values.targetType !== "SPECIFIC" ||
                  (formik.values.targetType === "SPECIFIC" &&
                    (formik.values.notification_trigger === "send_now" ||
                      (formik.values.notification_trigger === "send_later" &&
                        formik.values.notificationType === "NON_RECURRING" &&
                        formik.values.scheduled_date_and_time &&
                        formik.values.scheduled_date_and_time.length > 0) ||
                      (formik.values.notification_trigger === "send_later" &&
                        formik.values.notificationType === "RECURRING" &&
                        formik.values.recurDatesAndTimes &&
                        formik.values.recurDatesAndTimes.length > 0)))
                )
              }
            >
              {formik.isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Sending...
                </>
              ) : (
                "Send"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default NotificationModal;
